import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Truck,
  CheckCircle,
  X,
  User,
  MapPin,
  Calendar,
  IndianRupee,
  FileText,
  Copy,
  Check,
  AlertCircle,
  Edit,
  Save
} from 'lucide-react';
import { Order, OrderStatus } from '../types/order';
import { OrderService } from '../services/orderService';
import { formatDate } from '../utils/date';
import { formatPrice } from '../utils/utility';
import ShippingDetailsModal from '../components/admin/ShippingDetailsModal';
import useToastStore from '../store/useToastStore';

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToastStore();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<() => void>(() => { });
  const [confirmationMessage, setConfirmationMessage] = useState<string>("");
  const [isEditingDelivery, setIsEditingDelivery] = useState(false);
  const [newDeliveryDate, setNewDeliveryDate] = useState<string>("");

  useEffect(() => {
    let prevScrollRestoration: string | undefined;
    if ('scrollRestoration' in window.history) {
      prevScrollRestoration = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
    }
    // Use a timeout to ensure it runs after browser's restoration
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 20);
    return () => {
      if (prevScrollRestoration) {
        window.history.scrollRestoration = prevScrollRestoration as ScrollRestoration;
      }
    };
  }, []);

  useEffect(() => {
    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);

  useEffect(() => {
    if (order) {
      setNewDeliveryDate(order.expectedDeliveryDate.split('T')[0]);
    }
  }, [order]);

  const handleBackNavigation = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/admin/orders');
    }
  };

  const fetchOrder = async () => {
    try {
      setLoading(true);
      // Assuming we have a getOrderById method in OrderService
      const orderData = await OrderService.getOrderById(orderId!);
      setOrder(orderData);
    } catch (err) {
      setError('Failed to load order details');
      console.error('Error fetching order:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: OrderStatus, shippingDetails?: any) => {
    if (!order) return;

    try {
      await OrderService.updateOrderStatus(order._id, newStatus, shippingDetails);
      showToast('Order status updated successfully', 'success');
      fetchOrder(); // Refresh order data
    } catch (error) {
      showToast('Failed to update order status', 'error');
      console.error('Error updating order status:', error);
    }
  };

  const handleUpdateDeliveryDate = async () => {
    if (!order || !newDeliveryDate) return;

    try {
      const updatedDate = new Date(newDeliveryDate).toISOString();
      await OrderService.updateExpectedDeliveryDate(order._id, updatedDate);
      showToast('Expected delivery date updated successfully', 'success');
      setIsEditingDelivery(false);
      fetchOrder(); // Refresh order data
    } catch (error) {
      showToast('Failed to update delivery date', 'error');
      console.error('Error updating delivery date:', error);
    }
  };

  const handleConfirmAction = (action: () => void, message: string) => {
    setConfirmationAction(() => action);
    setConfirmationMessage(message);
    setShowConfirmationModal(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard', 'success');
  };

  const getStatusBadge = (status: OrderStatus) => {
    const statusConfig = {
      NEW: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', label: 'New' },
      PENDING: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Pending' },
      CONFIRMED: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Confirmed' },
      SHIPPED: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', label: 'Shipped' },
      DELIVERED: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Delivered' },
      CANCELLED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Cancelled' }
    };

    const config = statusConfig[status];
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text} ${config.border}`}>
        {config.label}
      </span>
    );
  };

  const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
      case 'NEW': return <AlertCircle className="w-5 h-5" />;
      case 'PENDING': return <AlertCircle className="w-5 h-5" />;
      case 'CONFIRMED': return <CheckCircle className="w-5 h-5" />;
      case 'SHIPPED': return <Truck className="w-5 h-5" />;
      case 'DELIVERED': return <CheckCircle className="w-5 h-5" />;
      case 'CANCELLED': return <X className="w-5 h-5" />;
      default: return <AlertCircle className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-500 mb-4">{error || 'The order you are looking for does not exist.'}</p>
          <button
            onClick={handleBackNavigation}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleBackNavigation}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Orders
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Order #{order.orderId}</h1>
              <p className="text-gray-500 mt-1">Placed on {formatDate(order.createdAt)}</p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center gap-4">
              {getStatusBadge(order.status)}
              <button
                onClick={() => copyToClipboard(order.orderId)}
                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                <Copy className="w-4 h-4 mr-1" />
                Copy ID
              </button>
            </div>
          </div>
        </div>

        {/* Main Content - Flat Design */}
        <div className="space-y-8">
          {/* Order Items Section */}
          <div className="bg-white border-b border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Order Items</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {order.items.map((item, index) => (
                <div key={item._id} className="px-6 py-4">
                  <div className="flex items-center space-x-4">
                    <img
                      src={item.images[0] || '/placeholder-product.png'}
                      alt={item.title}
                      className="w-16 h-16 object-cover rounded-md"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 truncate">{item.title}</h3>
                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                      {item.attributes && Object.keys(item.attributes).length > 0 && (
                        <div className="mt-1">
                          {Object.entries(item.attributes).map(([key, value]) => (
                            <span key={key} className="inline-block bg-gray-100 rounded-full px-2 py-1 text-xs text-gray-600 mr-2 mb-1">
                              {key}: {value}
                            </span>
                          ))}
                        </div>
                      )}
                      {item.discountPercent > 0 && (
                        <div className="mt-1">
                          <span className="text-xs text-green-600 font-medium">
                            {item.discountPercent}% off
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{formatPrice(item.discountedPrice * item.quantity)}</p>
                      <p className="text-sm text-gray-500 line-through">{formatPrice(item.mrp * item.quantity)}</p>
                      <p className="text-xs text-green-600">Save {formatPrice(item.discount * item.quantity)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Customer & Shipping Information - Merged */}
          <div className="bg-white border-b border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <User className="w-5 h-5 mr-2" />
                Customer & Shipping Information
              </h2>
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Customer Details */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Customer Details</h3>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-600">Name:</span>
                      <p className="text-sm font-medium text-gray-900">{order.shippingAddress.name}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Mobile:</span>
                      <p className="text-sm text-gray-900">{order.shippingAddress.mobile}</p>
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    Shipping Address
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-600">{order.shippingAddress.line1}</p>
                    {order.shippingAddress.line2 && (
                      <p className="text-gray-600">{order.shippingAddress.line2}</p>
                    )}
                    {order.shippingAddress.line3 && (
                      <p className="text-gray-600">{order.shippingAddress.line3}</p>
                    )}
                    <p className="text-gray-600">{order.shippingAddress.district}, {order.shippingAddress.state}</p>
                    <p className="text-gray-600">{order.shippingAddress.pincode}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment & Order Summary - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Payment Information */}
            <div className="bg-white border-b border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <IndianRupee className="w-5 h-5 mr-2" />
                  Payment Information
                </h2>
              </div>
              <div className="px-6 py-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Amount:</span>
                    <span className="text-sm font-medium text-gray-900">{formatPrice(order.amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Method:</span>
                    <span className="text-sm text-gray-900">{order.payment.paymentMethod}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Date:</span>
                    <span className="text-sm text-gray-900">{formatDate(order.payment.createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className={`text-sm font-medium ${order.payment.status === 'COMPLETED' ? 'text-green-600' :
                        order.payment.status === 'PENDING' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                      {order.payment.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white border-b border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Order Summary
                </h2>
              </div>
              <div className="px-6 py-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Subtotal:</span>
                    <span className="text-sm text-gray-900">
                      {formatPrice(order.items.reduce((total, item) => total + (item.mrp * item.quantity), 0))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Discount:</span>
                    <span className="text-sm text-green-600">
                      -{formatPrice(order.items.reduce((total, item) => total + (item.discount * item.quantity), 0))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Delivery Charge:</span>
                    <span className="text-sm text-gray-900">{formatPrice(order.deliveryCharge)}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between">
                      <span className="text-base font-medium text-gray-900">Total:</span>
                      <span className="text-base font-medium text-gray-900">{formatPrice(order.amount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Expected Delivery & Order Actions - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Expected Delivery */}
            <div className="bg-white border-b border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center justify-between">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Expected Delivery
                  </div>
                  <button
                    onClick={() => setIsEditingDelivery(!isEditingDelivery)}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900"
                  >
                    {isEditingDelivery ? (
                      <>
                        <X className="w-3 h-3 mr-1" />
                        Cancel
                      </>
                    ) : (
                      <>
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </>
                    )}
                  </button>
                </h2>
              </div>
              <div className="px-6 py-4">
                <div className="space-y-3">
                  {isEditingDelivery ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Expected Delivery Date
                        </label>
                        <input
                          type="date"
                          value={newDeliveryDate}
                          onChange={(e) => setNewDeliveryDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleUpdateDeliveryDate}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700"
                        >
                          <Save className="w-3 h-3 mr-1" />
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingDelivery(false);
                            setNewDeliveryDate(order.expectedDeliveryDate.split('T')[0]);
                          }}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <span className="text-sm text-gray-600">Expected Date:</span>
                      <p className="text-sm text-gray-900 mt-1">{formatDate(order.expectedDeliveryDate)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Order Actions */}
            <div className="bg-white border-b border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Order Actions</h2>
              </div>
              <div className="px-6 py-4">
                <div className="flex flex-wrap gap-3">
                  {order.status === 'NEW' && (
                    <button
                      onClick={() => handleConfirmAction(() => handleStatusUpdate('PENDING'), 'Are you sure you want to verify payment for this order?')}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Verify Payment
                    </button>
                  )}

                  {order.status === 'PENDING' && (
                    <button
                      onClick={() => handleConfirmAction(() => handleStatusUpdate('CONFIRMED'), 'Are you sure you want to confirm this order?')}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Confirm Order
                    </button>
                  )}

                  {order.status === 'CONFIRMED' && (
                    <button
                      onClick={() => setShowShippingModal(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <Package className="w-4 h-4 mr-2" />
                      Mark as Shipped
                    </button>
                  )}

                  {order.status === 'SHIPPED' && (
                    <button
                      onClick={() => handleConfirmAction(() => handleStatusUpdate('DELIVERED'), 'Are you sure you want to mark this order as delivered?')}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark as Delivered
                    </button>
                  )}

                  {['NEW', 'PENDING', 'CONFIRMED'].includes(order.status) && (
                    <button
                      onClick={() => handleConfirmAction(() => handleStatusUpdate('CANCELLED'), 'Are you sure you want to cancel this order? This action cannot be undone.')}
                      className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel Order
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showShippingModal && (
        <ShippingDetailsModal
          isOpen={showShippingModal}
          orderId={order.orderId}
          onClose={() => setShowShippingModal(false)}
          onConfirm={(shippingDetails) => {
            handleStatusUpdate('SHIPPED', shippingDetails);
            setShowShippingModal(false);
          }}
        />
      )}

      {showConfirmationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Action</h3>
              <p className="text-sm text-gray-500 mb-6">{confirmationMessage}</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    confirmationAction();
                    setShowConfirmationModal(false);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail; 